// src/pages/WritePost/index.jsx
import React, { useRef, useState } from 'react';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Divider, Input, Select, Space, Form, message, Upload, Modal } from 'antd';
import './index.css';

let index = 0;

const WritePost = () => {
  const [form] = Form.useForm();
  const [items, setItems] = useState(['#美食', '#生活']);
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const inputRef = useRef(null);

  const onNameChange = event => {
    setName(event.target.value);
  };

  const addItem = e => {
    e.preventDefault();
    setItems([...items, name || `New item ${index++}`]);
    setName('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  // 图片上传前的验证
  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('只能上传 JPG/PNG 格式的图片!');
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('图片大小不能超过 2MB!');
      return false;
    }
    return isJpgOrPng && isLt2M;
  };

  // 处理图片上传
  const handleImageUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const imgTag = `![图片](${e.target.result})\n`;
      const newContent = content + imgTag;
      setContent(newContent);
      form.setFieldsValue({ content: newContent }); // ✅ 关键：同步到 Form
      message.success(`${file.name} 上传成功`);
    };
    reader.readAsDataURL(file);
  };

  // 预览图片
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleSubmit = async (values) => {
    try {
      // 添加文章ID和日期
      const articleData = {
        ...values,
        id: Date.now(), // 使用时间戳作为ID
        date: new Date().toISOString().split('T')[0],
        tags: Array.isArray(values.tags) ? values.tags : values.tags.split(',').map(tag => tag.trim()).filter(tag => tag !== ''),
      };

      // 使用 localStorage 代替真实 API
      let articles = JSON.parse(localStorage.getItem('articles') || '[]');
      articles.push(articleData);
      localStorage.setItem('articles', JSON.stringify(articles));

      message.success('文章发布成功！');
      form.resetFields();
    } catch (error) {
      message.error('发布失败，请重试');
      console.error('发布文章失败:', error);
    }
  };

  return (
    <div className="input-container">
      <Form
        form={form}
        name="write_post"
        layout="vertical"
        onFinish={handleSubmit}
        style={{ width: '100%', maxWidth: '600px' }}
      >
        <Form.Item label="文章标题" name="title" rules={[{ required: true, message: '请输入文章标题' }]}>
          <Input placeholder="请输入文章标题" className="title-input" />
        </Form.Item>

        <Form.Item label="标签" name="tags" rules={[{ required: true, message: '请选择或输入至少一个标签！' }]}>
          <Select
            style={{ width: 300 }}
            placeholder="至少选择一个标签"
            popupRender={menu => (
              <>
                {menu}
                <Divider style={{ margin: '8px 0' }} />
                <Space style={{ padding: '0 8px 4px' }}>
                  <Input
                    placeholder="添加标签"
                    ref={inputRef}
                    value={name}
                    onChange={onNameChange}
                    onKeyDown={e => e.stopPropagation()}
                  />
                  <Button type="text" icon={<PlusOutlined />} onClick={addItem}>
                    Add item
                  </Button>
                </Space>
              </>
            )}
            options={items.map(item => ({ label: item, value: item }))}
          />
        </Form.Item>

        <Form.Item label="文章摘要" name="abstract" rules={[{ required: true, message: '请输入文章摘要不超过200字' }]}>
          <Input.TextArea 
            placeholder="请输入文章摘要不超过200字" 
            autoSize={{ minRows: 2, maxRows: 4 }} 
            className="abstract-input"
          />
        </Form.Item>

        <Form.Item label="文章内容" name="content" rules={[{ required: true, message: '请输入文章内容' }]}>
          <div style={{ marginBottom: '10px' }}>
            <Upload
              name="image"
              listType="picture-card"
              className="avatar-uploader"
              showUploadList={true}
              beforeUpload={beforeUpload}
              onChange={({ file }) => {
                if (file.status === 'done') {
                  handleImageUpload(file.originFileObj);
                }
              }}
              onPreview={handlePreview}
            >
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>上传图片</div>
              </div>
            </Upload>
          </div>
          <Input.TextArea 
            placeholder="请输入文章内容"
            autoSize={{ minRows: 8, maxRows: 20 }}
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              form.setFieldsValue({ content: e.target.value }); // ✅ 关键：实时同步
            }}
            className="content-input"
          />
        </Form.Item>

        <Form.Item>
          <Button 
            type="primary" 
            htmlType="submit"
            style={{ 
              backgroundColor: '#ff69b4',
              borderColor: '#ff69b4'
            }}
            className="submit-button"
          >
            发布文章
          </Button>
        </Form.Item>

        {/* 图片预览 Modal */}
        <Modal
          visible={previewVisible}
          title={previewTitle}
          footer={null}
          onCancel={() => setPreviewVisible(false)}
        >
          <img alt="preview" style={{ width: '100%' }} src={previewImage} />
        </Modal>
      </Form>
    </div>
  );
};

export default WritePost;